import {
  AfterViewInit,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  WritableSignal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { of, Subject } from 'rxjs';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
  BreadCrumb,
  IconType,
  IoniconsOutline,
  LoggedUser,
  NavItem,
  NavItemsId,
  NavItemsType,
  PtConsoleRoutes,
  PtPermissions,
  PtUiAppRoutes,
  SessionNotification,
} from '@pt/pt-models';
import { OrganizationsService, SwitchLanguageService } from '@pt/api-client';
import { buildBreadCrumb, NavUtils, ResolutionChecker } from '@pt/pt-shared';
import { environment } from '../../../environments/environment';
import {
  AppPolling,
  NgrxCoreReducer,
  PtCoreActions,
  PtCoreSelectors,
  UrlUtils,
} from '@pt/pt-core';
import { Organization } from 'it/primatoo/openapi/api/org/schemas/organization/Organization';
import { PrimatooModule } from 'it/primatoo/openapi/api/org/schemas/common/PrimatooModule';

@Component({
  selector: 'pt-dashboard',
  template: `
    <div
      style="
        position: relative;
        height: 100vh;
        width: 100vw;
        overflow: hidden;
        display: block;
       "
    >
      <pt-dashboard-layout
        [breadcrumbs]="breadcrumbs"
        [sidebarItems]="sidebarItems"
        [navbarItemsLeft]="navbarItemsLeft"
        [navbarItemsRight]="navbarItemsRight"
        [isOpenSessionInfo]="isOpenSessionInfo$ | async"
        [sessionNotifications]="sessionNotifications$ | async"
        [version]="version"
        [storageInfo]="storageInfo$ | async"
        (deleteSessionInfo)="deleteSessionInfo($event)"
        (clickCloseSessionInfo)="closeSessionInfoPanel()"
      >
        <router-outlet></router-outlet>
      </pt-dashboard-layout>
    </div>
  `,
})
export class DashboardComponent
  extends ResolutionChecker
  implements AfterViewInit, OnInit, OnDestroy
{
  public isOpenSessionInfo$ = this.store.select(
    PtCoreSelectors.isOpenSessionInfo
  );
  public sessionNotifications$ = this.store.select(
    PtCoreSelectors.sessionNotifications
  );
  public storageInfo$ = this.store.select(
    PtCoreSelectors.organizationStorageInfo
  );
  private organizationName$ = this.store.select(
    PtCoreSelectors.organizationName
  );
  private organizationResellerLogo$ = this.store.select(
    PtCoreSelectors.organizationResellerLogo
  );
  public loginResponse$ = this.store.select(PtCoreSelectors.loginResponse);
  private legalEntity$ = this.store.select(PtCoreSelectors.legalEntity);
  private anyPermission$ = this.store.select(PtCoreSelectors.anyPermission);
  private loginModules$ = this.store.select(PtCoreSelectors.loginModules);

  private organization$ = this.store.select(PtCoreSelectors.organization);
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private loginResponse?: LoggedUser;
  private isActiveMicrosoftModule$: WritableSignal<boolean> = signal(false);

  isAdmin = false; // Da modificare quando l'utente accede all'area amministratore e ricaricare loadSidebarItem()
  sidebarItems: NavItem[];
  navbarItemsLeft: NavItem[];
  navbarItemsRight: NavItem[];
  breadcrumbs: BreadCrumb[];
  organization: Organization;
  version = '1.0.0';

  private inactivityTime = environment.inactivityTime;
  private timer;

  @HostListener('document:keydown')
  keyDown() {
    this.resetTimer();
  }

  @HostListener('document:mousemove')
  onMouseMove() {
    this.resetTimer();
  }

  private resetTimer(updateSessionStorage = true) {
    this.destroyTimerIfExist(updateSessionStorage);
    this.timer = setTimeout(() => {
      if (this.eachTabIsInactive()) {
        this.store.dispatch(
          PtCoreActions.logoutAndClearDataForInactivityTime({
            route: [PtConsoleRoutes.LOGIN],
            domain: UrlUtils.getDomainIfExists(),
            userId: this.loginResponse?.id,
            url: window.location.href,
          })
        );
        this.destroyTimerIfExist(false);
      } else {
        this.resetTimer(false);
      }
    }, this.inactivityTime);
  }

  private eachTabIsInactive(): boolean {
    const latestSessionAction = Number.parseFloat(
      localStorage.getItem('latestAction')
    );
    if (latestSessionAction > 0) {
      const diff = Date.now() - latestSessionAction;
      return diff > this.inactivityTime;
    }
    return true;
  }

  private destroyTimerIfExist(updateSessionStorage = true) {
    if (this.timer) {
      clearTimeout(this.timer);
      if (updateSessionStorage) {
        localStorage.setItem('latestAction', Date.now() + '');
      }
    }
  }

  constructor(
    public renderer2: Renderer2,
    private store: Store<NgrxCoreReducer.CoreState>,
    public router: Router,
    private activatedRoute: ActivatedRoute,
    private switchLanguageService: SwitchLanguageService,
    private appPolling: AppPolling,
    private orgService: OrganizationsService,
    private translate: TranslateService
  ) {
    super(renderer2, router);
    switchLanguageService.initLanguages();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroyTimerIfExist();
    this.appPolling.stop();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.initializeResolution());
  }

  ngOnInit(): void {
    this.appPolling.start();
    this.resetTimer();
    this.loadLegalEntity();
    this.loadVersion();

    this.loginResponse$.subscribe((loginResponse: LoggedUser) => {
      this.loginResponse = loginResponse;
      if (loginResponse) {
        this.switchLanguageService.secureUpdateLanguage(loginResponse.language);
      } else {
        this.switchLanguageService.initLanguages();
      }
    });

    this.organization$
      .pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$),
        filter((conf: Organization | undefined) => !!conf)
      )
      .subscribe((organization: Organization) => {
        this.organization = organization;
        this.loadSidebarItem();
        this.loadNavbarItem();
        this.loadBreadCrumbAndProcessUrl();
      });

    this.loginModules$
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((modules: PrimatooModule[]) => {
        this.isActiveMicrosoftModule$.set(
          !!modules.find((m) => m === PrimatooModule.LOGIN_MS) ?? false
        );
        this.loadSidebarItem();
        this.loadNavbarItem();
        this.loadBreadCrumbAndProcessUrl();
      });
  }

  private loadLegalEntity() {
    this.store.dispatch(PtCoreActions.getLegalEntity());
  }

  private loadBreadCrumbAndProcessUrl() {
    this.breadcrumbs = buildBreadCrumb(this.activatedRoute.root);
    this.processUrl(this.router.url);
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.processUrl(this.router.url);
        this.breadcrumbs = buildBreadCrumb(this.activatedRoute.root);
        this.store.dispatch(PtCoreActions.closeSessionNotifications());
      });
  }

  private loadNavbarItem() {
    this.loadLeftNavbarItem();
    this.loadRightNavbarItem();
  }

  private loadLeftNavbarItem() {
    this.navbarItemsLeft = [];
    const ID = NavItemsId;
    this.navbarItemsLeft.push(
      NavUtils.createLogo(
        ID.LOGO,
        of('reseller'),
        null,
        this.organizationResellerLogo$,
        PtUiAppRoutes.HOME
      )
    );
  }

  private loadRightNavbarItem() {
    this.navbarItemsRight = [];
    const ID = NavItemsId;
    const TYPE = NavItemsType;

    this.navbarItemsRight.push(
      NavUtils.create({
        id: ID.ANY_PERMISSION,
        type: TYPE.ANY_PERMISSION,
        label: of(null),
        data: this.anyPermission$,
      })
    );

    this.navbarItemsRight.push(
      NavUtils.create({
        id: ID.LEGAL_ENTITY,
        type: TYPE.LEGAL_ENTITY,
        label: of(null),
        data: this.legalEntity$,
      })
    );

    this.navbarItemsRight.push(
      NavUtils.create({
        id: ID.REPORTS,
        type: TYPE.BUTTON,
        tooltipEnabled: true,
        classCss: ['pt-btn-navbar'],
        visible: [],
        routerLink: `${PtUiAppRoutes.DATA_BREACHES}/${PtUiAppRoutes.INCIDENT_REPORT}`,
        label: this.translate.stream('SIDEBAR.' + ID.REPORTS),
        icon: { key: IoniconsOutline.report, type: IconType.IONIC_ICON },
      })
    );

    this.navbarItemsRight.push(
      NavUtils.create({
        id: ID.REMINDER,
        type: TYPE.BUTTON,
        tooltipEnabled: true,
        classCss: ['pt-btn-navbar'],
        visible: [],
        routerLink: PtUiAppRoutes.REMINDER,
        label: this.translate.stream('LABELS.' + ID.REMINDER),
        icon: { key: IoniconsOutline.calendarCheck, type: IconType.IONIC_ICON },
      })
    );

    this.navbarItemsRight.push(
      NavUtils.create({
        id: ID.ALARM,
        tooltipEnabled: true,
        type: TYPE.SESSION_NOTIFICATION,
        label: this.translate.stream('TITLE.NOTIFICATIONS'),
        icon: { key: IoniconsOutline.userCircle, type: IconType.IONIC_ICON },
      })
    );

    this.navbarItemsRight.push(
      NavUtils.create({
        id: ID.SUPPORT,
        type: TYPE.SUPPORT,
        label: of(null),
        // children: [supportLink],
        children: [],
        classCss: ['pt-btn-navbar'],
        icon: { key: 'support.svg', type: IconType.SVG, size: 22 },
      })
    );

    // id: ID.REMINDER,
    //   type: TYPE.BUTTON,
    //   tooltipEnabled: true,
    //   classCss: ['pt-btn-navbar'],
    //   visible: [],
    //   routerLink: PtUiAppRoutes.REMINDER,
    //   label: this.translate.stream('LABELS.' + ID.REMINDER),
    //   icon: { key: IoniconsOutline.calendarCheck, type: IconType.IONIC_ICON },


    this.navbarItemsRight.push(
      NavUtils.create({
        id: ID.DOWNLOAD_MANUAL,
        type: TYPE.BUTTON,
        tooltipEnabled: true,
        label: of(null),
        visible: [],
        classCss: ['pt-btn-navbar'],
        icon: { key: IoniconsOutline.download, type: IconType.IONIC_ICON },
      })
    );

    const profile = NavUtils.createLink(
      ID.PROFILE,
      this.translate.stream('NAVBAR.' + ID.PROFILE),
      null,
      [],
      [],
      PtUiAppRoutes.PROFILE
    );

    const signout = NavUtils.createLink(
      ID.SIGNOUT,
      this.translate.stream('NAVBAR.' + ID.SIGNOUT),
      null,
      [],
      [],
      PtUiAppRoutes.LOGIN
    );

    const children: NavItem[] = [];
    children.push(profile);
    children.push(signout);
    this.navbarItemsRight.push(
      NavUtils.create({
        id: ID.USER,
        type: TYPE.USER,
        label: of(null),
        data: this.loginResponse$,
        children,
      })
    );
  }

  private loadSidebarItem() {
    this.sidebarItems = [
      NavUtils.createLogo(
        NavItemsId.LOGO,
        this.organizationName$,
        null,
        this.organization.logo ? of(this.organization.logo) : undefined,
        PtUiAppRoutes.HOME
      ),
    ];
    this.loadSidebarUserItem();
    this.loadSidebarAdminItem();
  }

  private loadSidebarAdminItem() {
    const ID = NavItemsId;
    const usersLink = NavUtils.createLink(
      ID.USERS,
      this.translate.stream('SIDEBAR.' + ID.USERS),
      null,
      [],
      [],
      PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION + '/' + PtUiAppRoutes.USERS
    );

    const userGroupsLink = NavUtils.createLink(
      ID.USER_GROUPS,
      this.translate.stream('SIDEBAR.' + ID.USER_GROUPS),
      null,
      [],
      [],
      PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION +
        '/' +
        PtUiAppRoutes.USER_GROUPS
    );

    const settingLink = NavUtils.createLink(
      ID.SETTINGS,
      this.translate.stream('SIDEBAR.' + ID.SETTINGS),
      null,
      [],
      [],
      PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION +
        '/' +
        PtUiAppRoutes.SETTINGS
    );

    const legalBasisNavLink: NavItem = NavUtils.createLink(
      ID.LEGAL_BASIS,
      this.translate.stream('SIDEBAR.' + ID.LEGAL_BASIS),
      null,
      [],
      [],
      PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION +
        '/' +
        PtUiAppRoutes.LEGAL_BASIS
    );

    const organizationalMeasuresCategoriesLink = NavUtils.createLink(
      ID.SECURITY_MEASURE_CATEGORIES,
      this.translate.stream('SIDEBAR.' + ID.SECURITY_MEASURE_CATEGORIES),
      null,
      [],
      [],
      PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION +
        '/' +
        PtUiAppRoutes.SECURITY_MEASURE_CATEGORIES,
      false
      // ['sidebar-link--leaf']
    );

    const organizationalMeasuresLink = NavUtils.createLink(
      ID.SECURITY_MEASURES,
      this.translate.stream('SIDEBAR.' + ID.SECURITY_MEASURES),
      null,
      [],
      [],
      PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION +
        '/' +
        PtUiAppRoutes.SECURITY_MEASURES
    );

    const preliminaryRiskAssessmentNavLink: NavItem = NavUtils.createLink(
      ID.PRELIMINARY_RISK_ASSESSMENTS,
      this.translate.stream('SIDEBAR.' + ID.PRELIMINARY_RISK_ASSESSMENTS),
      null,
      [],
      [],
      PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION +
        '/' +
        PtUiAppRoutes.PRELIMINARY_RISK_ASSESSMENTS
    );

    const technicalMeasureCategoriesNavLink: NavItem = NavUtils.createLink(
      ID.TECHNICAL_MEASURE_CATEGORIES,
      this.translate.stream('SIDEBAR.' + ID.TECHNICAL_MEASURE_CATEGORIES),
      null,
      [],
      [],
      PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION +
        '/' +
        PtUiAppRoutes.TECHNICAL_MEASURE_CATEGORIES,
      false
      // ['sidebar-link--leaf']
    );

    const technicalMeasureNavLink: NavItem = NavUtils.createLink(
      ID.TECHNICAL_MEASURES,
      this.translate.stream('SIDEBAR.' + ID.TECHNICAL_MEASURES),
      null,
      [],
      [],
      PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION +
        '/' +
        PtUiAppRoutes.TECHNICAL_MEASURES
    );

    const natureOfDataNavLink: NavItem = NavUtils.createLink(
      ID.NATURE_OF_DATA,
      this.translate.stream('SIDEBAR.' + ID.NATURE_OF_DATA),
      null,
      [],
      [],
      PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION +
        '/' +
        PtUiAppRoutes.NATURE_OF_DATA
    );

    const interestedPersonCategoryNavLink: NavItem = NavUtils.createLink(
      ID.INTERESTED_PERSON_CATEGORY,
      this.translate.stream('SIDEBAR.' + ID.INTERESTED_PERSON_CATEGORY),
      null,
      [],
      [],
      PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION +
        '/' +
        PtUiAppRoutes.INTERESTED_PERSON_CATEGORIES
    );

    const damagesLink = NavUtils.createLink(
      ID.DAMAGES,
      this.translate.stream('SIDEBAR.' + ID.DAMAGES),
      null,
      [],
      [],
      PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION +
        '/' +
        PtUiAppRoutes.DAMAGES
    );

    const mailHistoryLink = NavUtils.createLink(
      ID.MAIL_HISTORY,
      this.translate.stream('SIDEBAR.' + ID.MAIL_HISTORY),
      null,
      [],
      [],
      PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION +
        '/' +
        PtUiAppRoutes.MAIL_HISTORY
    );

    const permissionRulesLink = NavUtils.createLink(
      ID.PERMISSION_RULES,
      this.translate.stream('SIDEBAR.' + ID.PERMISSION_RULES),
      null,
      [],
      [],
      PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION +
        '/' +
        PtUiAppRoutes.PERMISSION_RULES
    );
    const assessmentQuestionsLink = NavUtils.createLink(
      ID.ASSESSMENT_QUESTION,
      this.translate.stream('SIDEBAR.' + ID.ASSESSMENT_QUESTION),
      null, //IoniconsOutline.helpCircle,
      [],
      [PtPermissions.ENABLE_FOR_SYSTEM],
      PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION +
        '/' +
        PtUiAppRoutes.ASSESSMENT_QUESTION,
      true,
      ['sidebar-menu--no-separator']
    );

    const sLinks: NavItem[] = [
      organizationalMeasuresLink,
      organizationalMeasuresCategoriesLink,
      technicalMeasureNavLink,
      technicalMeasureCategoriesNavLink,
      preliminaryRiskAssessmentNavLink,
      interestedPersonCategoryNavLink,
      natureOfDataNavLink,
      damagesLink,
      legalBasisNavLink,
      mailHistoryLink,
    ];
    const lr = this.loginResponse;

    if (
      lr && (lr.system || lr.permissions.admin || !this.isActiveMicrosoftModule$())
    ) {
      sLinks.push(usersLink);
    }
    sLinks.push(
      ...[
        userGroupsLink,
        permissionRulesLink,
        assessmentQuestionsLink,
        settingLink,
      ]
    );

    this.sidebarItems.push(
      NavUtils.createMenu(
        NavItemsId.ADMINISTRATION_AND_CONFIGURATION,
        this.translate.stream(
          'SIDEBAR.' + NavItemsId.ADMINISTRATION_AND_CONFIGURATION
        ),
        IoniconsOutline.build,
        [],
        [PtPermissions.ENABLE_FOR_SYSTEM, PtPermissions.ENABLE_FOR_ADMIN],
        sLinks,
        PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION,
        null
      )
    );
  }

  private loadSidebarUserItem() {
    const ID = NavItemsId;
    this.sidebarItems.push(
      NavUtils.createLink(
        ID.HOME,
        this.translate.stream('SIDEBAR.' + ID.HOME),
        IoniconsOutline.home,
        [],
        [],
        PtUiAppRoutes.HOME,
        true
      )
    );

    const OrganizationNavLinkSeparator: NavItem = NavUtils.createLinkSeparator(
      ID.ORGANIZATION,
      this.translate.stream('SIDEBAR.' + ID.ORGANIZATION)
    );

    const AssetNavLinkSeparator: NavItem = NavUtils.createLinkSeparator(
      ID.ASSET,
      this.translate.stream('SIDEBAR.' + ID.ASSET)
    );

    const ProcessNavLinkSeparator: NavItem = NavUtils.createLinkSeparator(
      ID.PROCESS,
      this.translate.stream('SIDEBAR.' + ID.PROCESS)
    );

    const legalEntityNavLink: NavItem = NavUtils.createLink(
      ID.LEGAL_ENTITY,
      this.translate.stream('SIDEBAR.' + ID.LEGAL_ENTITY),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE +
        '/' +
        PtUiAppRoutes.LEGAL_ENTITY
    );

    const treatmentCategoryNavLink: NavItem = NavUtils.createLink(
      ID.TREATMENT_CATEGORY,
      this.translate.stream('SIDEBAR.' + ID.TREATMENT_CATEGORY),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE +
        '/' +
        PtUiAppRoutes.TREATMENT_CATEGORY
    );

    const departmentNavLink: NavItem = NavUtils.createLink(
      ID.DEPARTMENTS,
      this.translate.stream('SIDEBAR.' + ID.DEPARTMENTS),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE + '/' + PtUiAppRoutes.DEPARTMENTS
    );

    const regulationsNavLink: NavItem = NavUtils.createLink(
      ID.REGULATIONS,
      this.translate.stream('SIDEBAR.' + ID.REGULATIONS),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE + '/' + PtUiAppRoutes.REGULATIONS
    );

    const applicationsNavLink: NavItem = NavUtils.createLink(
      ID.APPLICATIONS,
      this.translate.stream('SIDEBAR.' + ID.APPLICATIONS),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE +
        '/' +
        PtUiAppRoutes.APPLICATIONS
    );

    const recipientsNavLink: NavItem = NavUtils.createLink(
      ID.RECIPIENTS,
      this.translate.stream('SIDEBAR.' + ID.RECIPIENTS),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE + '/' + PtUiAppRoutes.RECIPIENTS
    );

    const noticesNavLink: NavItem = NavUtils.createLink(
      ID.NOTICES,
      this.translate.stream('SIDEBAR.' + ID.NOTICES),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE + '/' + PtUiAppRoutes.NOTICES
    );

    const organizationalFunctionNavLink: NavItem = NavUtils.createLink(
      ID.ORGANIZATIONAL_FUNCTIONS,
      this.translate.stream('SIDEBAR.' + ID.ORGANIZATIONAL_FUNCTIONS),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE +
        '/' +
        PtUiAppRoutes.ORGANIZATIONAL_FUNCTIONS
    );

    const privacyRoleNavLink: NavItem = NavUtils.createLink(
      ID.PRIVACY_ROLES,
      this.translate.stream('SIDEBAR.' + ID.PRIVACY_ROLES),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE +
        '/' +
        PtUiAppRoutes.PRIVACY_ROLES,
      false
      // ['sidebar-link--leaf']
    );

    const employmentRelationshipNavLink: NavItem = NavUtils.createLink(
      ID.EMPLOYMENT_RELATIONSHIP,
      this.translate.stream('SIDEBAR.' + ID.EMPLOYMENT_RELATIONSHIP),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE +
        '/' +
        PtUiAppRoutes.EMPLOYMENT_RELATIONSHIP,
      false
      // ['sidebar-link--leaf']
    );

    const supplierNavLink: NavItem = NavUtils.createLink(
      ID.SUPPLIERS,
      this.translate.stream('SIDEBAR.' + ID.SUPPLIERS),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE + '/' + PtUiAppRoutes.SUPPLIERS
    );

    const applicationTypesNavLink: NavItem = NavUtils.createLink(
      ID.APPLICATION_TYPES,
      this.translate.stream('SIDEBAR.' + ID.APPLICATION_TYPES),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE +
        '/' +
        PtUiAppRoutes.APPLICATION_TYPES,
      false
      // ['sidebar-link--leaf']
    );

    const usersResourcesLink = NavUtils.createLink(
      ID.USERS_RESOURCES,
      this.translate.stream('SIDEBAR.' + ID.USERS_RESOURCES),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE +
        '/' +
        PtUiAppRoutes.USERS_RESOURCES
    );

    const purposeNavLink: NavItem = NavUtils.createLink(
      ID.PURPOSE_CATEGORIES,
      this.translate.stream('SIDEBAR.' + ID.PURPOSE_CATEGORIES),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE +
        '/' +
        PtUiAppRoutes.PURPOSE_CATEGORIES
    );

    this.sidebarItems.push(
      NavUtils.createMenu(
        ID.DATA_PROTECTION_GOVERNANCE,
        this.translate.stream('SIDEBAR.' + ID.DATA_PROTECTION_GOVERNANCE),
        IoniconsOutline.archive,
        [],
        [],
        [
          OrganizationNavLinkSeparator,

          legalEntityNavLink,
          organizationalFunctionNavLink,
          usersResourcesLink,
          privacyRoleNavLink,
          employmentRelationshipNavLink,

          AssetNavLinkSeparator,

          applicationsNavLink,
          applicationTypesNavLink,

          ProcessNavLinkSeparator,

          regulationsNavLink,
          noticesNavLink,
          purposeNavLink,
          treatmentCategoryNavLink,
          departmentNavLink,
          recipientsNavLink,
          supplierNavLink,
        ],
        PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE
      )
    );

    const RegistersAndAppointmentsNavLinkSeparator: NavItem =
      NavUtils.createLinkSeparator(
        ID.REGISTERS_AND_APPOINTMENTS,
        this.translate.stream('SIDEBAR.' + ID.REGISTERS_AND_APPOINTMENTS)
      );

    const ModulesNavLinkSeparator: NavItem = NavUtils.createLinkSeparator(
      ID.OTHER_MODULES,
      this.translate.stream('SIDEBAR.' + ID.OTHER_MODULES)
    );

    const treatmentHolderNavLink: NavItem = NavUtils.createLink(
      ID.TREATMENTS_HOLDER,
      this.translate.stream('SIDEBAR.' + ID.TREATMENTS_HOLDER),
      null,
      [],
      [],
      PtUiAppRoutes.BUSINESS_MANAGEMENT + '/' + PtUiAppRoutes.TREATMENTS_HOLDER
    );

    const treatmentResponsibleNavLink: NavItem = NavUtils.createLink(
      ID.TREATMENTS_RESPONSIBLE,
      this.translate.stream('SIDEBAR.' + ID.TREATMENTS_RESPONSIBLE),
      null,
      [],
      [],
      PtUiAppRoutes.BUSINESS_MANAGEMENT +
        '/' +
        PtUiAppRoutes.TREATMENTS_RESPONSIBLE
    );

    const treatmentSubResponsibleNavLink: NavItem = NavUtils.createLink(
      ID.TREATMENTS_SUB_RESPONSIBLE,
      this.translate.stream('SIDEBAR.' + ID.TREATMENTS_SUB_RESPONSIBLE),
      null,
      [],
      [],
      PtUiAppRoutes.BUSINESS_MANAGEMENT +
        '/' +
        PtUiAppRoutes.TREATMENTS_SUB_RESPONSIBLE
    );

    const dpiaNavLink: NavItem = NavUtils.createLink(
      ID.DPIA,
      this.translate.stream('SIDEBAR.' + ID.DPIA),
      null,
      [],
      [],
      PtUiAppRoutes.BUSINESS_MANAGEMENT + '/' + PtUiAppRoutes.DPIA
    );

    const liaNavLink: NavItem = NavUtils.createLink(
      ID.LIA,
      this.translate.stream('SIDEBAR.' + ID.LIA),
      null,
      [],
      [],
      PtUiAppRoutes.BUSINESS_MANAGEMENT + '/' + PtUiAppRoutes.LIA
    );

    const appAssessmentNavLink: NavItem = NavUtils.createLink(
      ID.APP_ASSESSMENTS,
      this.translate.stream('SIDEBAR.' + ID.APP_ASSESSMENTS),
      null,
      [],
      [],
      PtUiAppRoutes.BUSINESS_MANAGEMENT + '/' + PtUiAppRoutes.APP_ASSESSMENTS
    );

    const aiAssessmentNavLink: NavItem = NavUtils.createLink(
      ID.AI_ASSESSMENTS,
      this.translate.stream('SIDEBAR.' + ID.AI_ASSESSMENTS),
      null,
      [],
      [],
      PtUiAppRoutes.BUSINESS_MANAGEMENT + '/' + PtUiAppRoutes.AI_ASSESSMENTS
    );

    const riskAssessmentNavLink: NavItem = NavUtils.createLink(
      ID.GLOBAL_MEASURE_CHECK,
      this.translate.stream('SIDEBAR.' + ID.GLOBAL_MEASURE_CHECK),
      null,
      [],
      [],
      PtUiAppRoutes.BUSINESS_MANAGEMENT +
        '/' +
        PtUiAppRoutes.GLOBAL_MEASURE_CHECK
    );

    const projectNavLink: NavItem = NavUtils.createLink(
      ID.PROJECT_CARDS,
      this.translate.stream('SIDEBAR.' + ID.PROJECT_CARDS),
      null,
      [],
      [],
      PtUiAppRoutes.BUSINESS_MANAGEMENT + '/' + PtUiAppRoutes.PROJECT_CARDS
    );

    const appointmentsNavLink: NavItem = NavUtils.createLink(
      ID.APPOINTMENTS,
      this.translate.stream('SIDEBAR.' + ID.APPOINTMENTS),
      null,
      [],
      [],
      PtUiAppRoutes.BUSINESS_MANAGEMENT + '/' + PtUiAppRoutes.APPOINTMENTS
    );

    const cyberSecurityNavLink: NavItem = NavUtils.createLink(
      ID.CYBER_SECURITY,
      this.translate.stream('SIDEBAR.' + ID.CYBER_SECURITY),
      null,
      [],
      [],
      PtUiAppRoutes.BUSINESS_MANAGEMENT + '/' + PtUiAppRoutes.CYBER_SECURITY,
      false,
      [],
      true
    );

    const covidAssessmentsNavLink: NavItem = NavUtils.createLink(
      ID.COVID_ASSESSMENTS,
      this.translate.stream('SIDEBAR.' + ID.COVID_ASSESSMENTS),
      null,
      [],
      [],
      PtUiAppRoutes.BUSINESS_MANAGEMENT + '/' + PtUiAppRoutes.COVID_ASSESSMENTS
    );

    const covidTemplateNavLink: NavItem = NavUtils.createLink(
      ID.COVID_TEMPLATE,
      this.translate.stream('SIDEBAR.' + ID.COVID_TEMPLATE),
      null,
      [PtPermissions.ACTIVE_MODULE_COVID],
      [],
      PtUiAppRoutes.BUSINESS_MANAGEMENT + '/' + PtUiAppRoutes.COVID_TEMPLATE
    );

    const DataBreachNavLinkSeparator: NavItem = NavUtils.createLinkSeparator(
      ID.SECURITY_INCIDENT,
      this.translate.stream('SIDEBAR.' + ID.SECURITY_INCIDENT)
    );

    const dataBreachNavLink = NavUtils.createLink(
      ID.DATA_BREACHES,
      this.translate.stream('SIDEBAR.' + ID.DATA_BREACHES),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_BREACHES + '/' + PtUiAppRoutes.DATA_BREACHES_REGISTER
    );

    const incidentTeamNavLink = NavUtils.createLink(
      ID.GENERIC_REPORT,
      this.translate.stream('SIDEBAR.' + ID.INCIDENT_TEAM),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_BREACHES + '/' + PtUiAppRoutes.INCIDENT_TEAM
    );

    const extractionNavLink: NavItem = NavUtils.createLink(
      ID.EXTRACTION,
      this.translate.stream('SIDEBAR.' + ID.EXTRACTION),
      null,
      [],
      [],
      PtUiAppRoutes.BUSINESS_MANAGEMENT + '/' + PtUiAppRoutes.EXTRACTION
    );

    // const genericReportNavLink = NavUtils.createLink(
    //   ID.GENERIC_REPORT,
    //   this.translate.stream('SIDEBAR.' + ID.GENERIC_REPORT),
    //   null,
    //   [PtPermissions.ACTIVE_MODULE_DATABREACH, PtPermissions.MANAGE_DATABREACH],
    //   [],
    //   PtUiAppRoutes.DATA_BREACHES + '/' + PtUiAppRoutes.GENERIC_REPORT
    // );

    const guaranteedCategoryNavLink = NavUtils.createLink(
      ID.GENERIC_REPORT,
      this.translate.stream('SIDEBAR.' + ID.GUARANTEED_CATEGORY),
      null,
      [],
      [],
      PtUiAppRoutes.DATA_BREACHES +
        '/' +
        PtUiAppRoutes.DATA_BREACH_CONFIGURATION
    );

    this.sidebarItems.push(
      NavUtils.createMenu(
        ID.BUSINESS_MANAGEMENT,
        this.translate.stream('SIDEBAR.' + ID.BUSINESS_MANAGEMENT),
        IoniconsOutline.building,
        [],
        [],
        [
          RegistersAndAppointmentsNavLinkSeparator,

          treatmentHolderNavLink,
          treatmentResponsibleNavLink,
          treatmentSubResponsibleNavLink,
          projectNavLink,
          dpiaNavLink,
          liaNavLink,
          appointmentsNavLink,

          appAssessmentNavLink,
          riskAssessmentNavLink,
          extractionNavLink,

          DataBreachNavLinkSeparator,
          dataBreachNavLink,
          incidentTeamNavLink,
          guaranteedCategoryNavLink,

          ModulesNavLinkSeparator,
          cyberSecurityNavLink,
          aiAssessmentNavLink,

          covidAssessmentsNavLink,
          covidTemplateNavLink,
        ],
        PtUiAppRoutes.BUSINESS_MANAGEMENT
      )
    );

    this.sidebarItems.push(
      NavUtils.createLink(
        ID.WORKSPACE,
        this.translate.stream('SIDEBAR.' + ID.WORKSPACE),
        IoniconsOutline.boxes,
        [],
        [],
        PtUiAppRoutes.WORKSPACE,
        true
      )
    );

    this.sidebarItems.push(
      NavUtils.createLink(
        ID.TUTORIALS,
        this.translate.stream('SIDEBAR.' + ID.TUTORIALS),
        IoniconsOutline.support,
        [],
        [PtPermissions.FORCE_TO_DISABLED],
        PtUiAppRoutes.TUTORIALS,
        true
      )
    );
  }

  private processUrl(url: string) {
    if (url) {
      switch (true) {
        case url.startsWith(
          '/' + PtUiAppRoutes.ADMINISTRATION_AND_CONFIGURATION
        ):
          if (!this.isAdmin) {
            this.isAdmin = true;
            // this.loadSidebarItem();
          }
          break;

        case this.notAdminAccessUrl(url):
          if (this.isAdmin) {
            this.isAdmin = false;
            // this.loadSidebarItem();
          }
          break;
      }
    }
  }

  closeSessionInfoPanel() {
    this.store.dispatch(PtCoreActions.closeSessionNotifications());
  }

  deleteSessionInfo(sessionInfo: SessionNotification[]) {
    sessionInfo?.forEach((si) => {
      this.store.dispatch(
        PtCoreActions.removeSessionNotifications({
          sessionNotificationId: si.id,
        })
      );
    });
  }

  setResolutionScaled(resolutionScaled: boolean) {
    this.store.dispatch(
      PtCoreActions.setResolutionScaled({ resolutionScaled })
    );
  }

  private notAdminAccessUrl(url: string) {
    return (
      url.startsWith('/' + PtUiAppRoutes.HOME) ||
      url.startsWith('/' + PtUiAppRoutes.DATA_PROTECTION_GOVERNANCE) ||
      url.startsWith('/' + PtUiAppRoutes.BUSINESS_MANAGEMENT) ||
      url.startsWith('/' + PtUiAppRoutes.WORKSPACE)
    );
  }

  private loadVersion() {
    this.orgService
      .getAppVersion()
      .subscribe((version: { appVersion: string }) => {
        this.version = version.appVersion;
      });
  }
}
